/*
Schroeder/Freeverb style reverb for the SF2 `reverbEffectsSend` bus.

The SF2 2.04 spec defines generator 16 only as a 0-1000 send level "to the reverb effects
processor" and deliberately mandates no algorithm, so the bank tells us how much reverb a
zone wants and nothing about what it should sound like. An FDN (parallel comb filters into
series allpass diffusion) costs O(1) per sample regardless of RT60 or voice count, and its
RT60 is a parameter rather than a property of an impulse response asset, which is what lets
the render window below be sized analytically.

The per-sample recurrence lives here as a plain function over Float32Arrays with no
AudioNode and no AudioContext, for two reasons: `lib`'s mocha environment has no Web Audio
polyfill and testing an impulse response in a plain array is more informative than testing
Web Audio plumbing, and the AudioWorklet is built by stringifying this same function into
the worklet blob (see reverb-worklet.civet), so the tested code and the running code are
the one implementation.

Everything here is deterministic: fixed delay lengths, fixed coefficients, no `Math.random`,
no wall clock, no dependence on the context's `currentTime`. Two renders of the same input
are sample-identical.

That is a claim about this function, not about a finished export. The surrounding Web Audio
graph is not bit-reproducible — Chromium sums three or more inputs into a node in an order
that varies between runs, and float addition is not associative. See "Render determinism" in
docs/engine-accuracy.md.
*/

/**
Target RT60 in seconds. MEASURED: the SC-55mk2's power-on Hall 2 decays ~2s (Nuked-SC55
render of a staccato hit at CC 91 = 64, tail tracked -33.5dB→-73dB over 1.5s), and GS-era
files are mixed against that room. The old 0.6s default read as a closet by comparison.
*/
export REVERB_RT60 := 2.0

/**
Longest RT60 the runtime accepts (`setRT60` clamps to it). GS reverb-time SysEx can ask
for longer than the default room; this is the ceiling the drain window is sized for.
*/
export REVERB_RT60_MAX := 3.0

/**
Seconds a render must carry past the last note for the tail to have decayed into inaudibility,
derived from the coefficients rather than measured.

Every comb's feedback is solved for exactly -60dB after its RT60 (see `combFeedback`),
and the comb bank is the only place energy is stored for longer than a few milliseconds —
the allpass chain is unity-gain and its longest delay is under 13ms. So at 1.5x RT60 the
worst case (DC, where the damping lowpass is transparent) is down 90dB, which is below the
16 bit floor of anything we export.

Sized for `REVERB_RT60_MAX`, not the default: a file can lengthen the room mid-song via
GS SysEx, and the drain has to cover whatever it may have asked for.
*/
export REVERB_DRAIN_SECONDS := REVERB_RT60_MAX * 1.5

/**
Feedback coefficient for a comb of `delay` samples that decays 60dB in `rt60` seconds.

`g^(rt60 * sampleRate / delay) = 10^(-3)`, i.e. the comb recirculates `rt60*fs/delay` times
before it must be 60dB down.

`createReverb` computes this inline rather than calling it — it has to be self contained to
survive `toString` into the worklet — so this exists for the drain window's derivation and
for the test that holds the two in agreement.
*/
export combFeedback := (delay: number, rt60: number, sampleRate: number) ->
  Math.pow 10, -3 * delay / (rt60 * sampleRate)

/**
Build a reverb processor.

Returns an object with `process(l, r)`, which advances the recurrence one sample and leaves
the wet output in `outL` / `outR`. Output is written to fields rather than returned as a pair
so that a per-sample call allocates nothing.

`damping` is the coefficient of the one pole lowpass inside each comb's feedback path — the
high end of the tail decays faster than the low end, as it does in a room. The lowpass is
unity gain at DC, so it can only ever shorten the decay, never lengthen it past the RT60 the
feedback coefficients were solved for.
*/
export createReverb := (sampleRate: number, rt60: number = REVERB_RT60, damping: number = 0.35, width: number = 1) ->
  combTuning := [1116, 1188, 1277, 1356, 1422, 1491, 1557, 1617]
  allpassTuning := [556, 441, 341, 225]
  stereoSpread := 23
  // Freeverb's own allpass coefficient. Diffusion only: the allpasses are flat in magnitude,
  // they smear the comb output in time so the early part of the tail is not a set of audible
  // discrete echoes.
  allpassFeedback := 0.5

  scale := sampleRate / 44100

  combs: { buffer: Float32Array, index: number, feedback: number, store: number }[] := []
  allpasses: { buffer: Float32Array, index: number }[] := []

  for let i = 0; i < combTuning.length; i++
    for let c = 0; c < 2; c++
      n := Math.max 1, Math.round((combTuning[i] + c * stereoSpread) * scale)
      combs.push
        buffer: new Float32Array n
        index: 0
        feedback: Math.pow(10, -3 * n / (rt60 * sampleRate))
        store: 0

  for let i = 0; i < allpassTuning.length; i++
    for let c = 0; c < 2; c++
      n := Math.max 1, Math.round((allpassTuning[i] + c * stereoSpread) * scale)
      allpasses.push
        buffer: new Float32Array n
        index: 0

  // 1 - damping on the direct term, damping on the held term: a one pole lowpass with unity
  // gain at DC.
  damp1 := damping
  damp2 := 1 - damping
  // Combs sum in parallel and each one has a DC gain of 1/(1-feedback), so the bank as a
  // whole is several times unity and gets hotter as RT60 rises. Normalising by the summed
  // DC gain makes a send level mean the same thing at any RT60, and keeps a bus fed by 16
  // channels away from the limiter.
  let dcGain = 0
  for let i = 0; i < combs.length; i += 2
    dcGain += 1 / (1 - combs[i].feedback)
  // `let`, not const: setRT60 below re-solves it when the decay is retuned.
  let combScale = 1 / dcGain

  state :=
    outL: 0
    outR: 0
    // Exposed so a test can check the coefficients the drain window is derived from.
    combs: combs
    // Retune the decay in place (GS reverb-time SysEx). Same solve as construction; the
    // stored samples keep ringing, so a change mid-tail glides rather than clicks. Must
    // stay self-contained — this whole factory is stringified into the worklet — which is
    // why the ceiling is a literal (it mirrors REVERB_RT60_MAX; the drain is sized to it).
    setRT60: (rt60: number) ->
      rt60 = Math.min Math.max(rt60, 0.1), 3.0
      let dc = 0
      for let i = 0; i < combs.length; i++
        comb := combs[i]
        comb.feedback = Math.pow 10, -3 * comb.buffer.length / (rt60 * sampleRate)
        if i % 2 is 0
          dc += 1 / (1 - comb.feedback)
      combScale = 1 / dc
      return
    // Mono sum feeding the bank. SF2 sends are a level, not a position — the room is shared,
    // and the stereo of the tail comes from the spread offsets, not from the input.
    process: (l: number, r: number) ->
      input := (l + r) * 0.5

      let accL = 0, accR = 0
      for let i = 0; i < combs.length; i += 2
        for let c = 0; c < 2; c++
          comb := combs[i + c]
          { buffer } := comb
          out := buffer[comb.index]
          comb.store = out * damp2 + comb.store * damp1
          buffer[comb.index] = input + comb.store * comb.feedback
          comb.index = (comb.index + 1) % buffer.length

          if c is 0
            accL += out
          else
            accR += out

      let wetL = accL * combScale, wetR = accR * combScale

      for let i = 0; i < allpasses.length; i += 2
        for let c = 0; c < 2; c++
          ap := allpasses[i + c]
          { buffer } := ap
          v := if c is 0 then wetL else wetR
          bufOut := buffer[ap.index]
          buffer[ap.index] = v + bufOut * allpassFeedback
          ap.index = (ap.index + 1) % buffer.length

          if c is 0
            wetL = bufOut - v
          else
            wetR = bufOut - v

      // `width` 1 leaves the two channels as the spread made them, 0 collapses to mono.
      state.outL = wetL * width + wetR * (1 - width)
      state.outR = wetR * width + wetL * (1 - width)

      return

  return state

export type Reverb = ReturnType<typeof createReverb>
